import { getFullName } from "@/entities/people/helpers";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { PersonData } from "@/entities/people/types";
import { TaskEntity, TaskLink } from "@/entities/tasks/types";
import { EntityData } from "@/services/api/types";
import { ENTITY_NAME_TO_PREFIX } from "@/services/api/entityPrefixes";
import {
  EntityType,
  entityTypeToPrefix,
  prefixToEntityType
} from "@/services/hooks/entities/entityTypes";

const getPrefix = (entityId: string) => `${entityId.split("-")[0]}-`;

/**
 * entityNames is the richer of the two name sources but is not always present;
 * an empty array counts as absent, so an older payload's entities still wins.
 */
export const getTaskEntityNames = (
  entityNames: TaskEntity[] | null | undefined,
  entities: TaskEntity[] | null | undefined
): TaskEntity[] | null | undefined =>
  entityNames?.length ? entityNames : entities;

export const getEntityTypeByEntityId = (
  entityId: string | undefined
): EntityType | undefined =>
  entityId ? prefixToEntityType[getPrefix(entityId)] : undefined;

const formatLinkName = (entityId: string, entityName: string) => {
  const prefix = getPrefix(entityId);
  const isPersonLike =
    prefix === ENTITY_NAME_TO_PREFIX.people ||
    prefix === ENTITY_NAME_TO_PREFIX.partners_contacts;
  const separatorIndex = entityName.indexOf(";");
  if (!isPersonLike || separatorIndex === -1) return entityName;
  return getFullName({
    firstName: entityName.slice(0, separatorIndex),
    lastName: entityName.slice(separatorIndex + 1)
  });
};

export const resolveAllTaskLinks = (
  entityNames: TaskEntity[] | null | undefined,
  entityIds: string[] | null | undefined
): TaskLink[] => {
  if (!entityIds) return [];

  const nameMap = new Map(
    (entityNames ?? []).map((entity) => [entity.entityId, entity])
  );

  return entityIds.map((entityId) => ({
    entityId,
    entityName: formatLinkName(
      entityId,
      nameMap.get(entityId)?.entityName ?? ""
    ),
    entityType: getEntityTypeByEntityId(entityId)
  }));
};

const legacySlotPrefixes = new Set(
  [
    EntityType.DEAL,
    EntityType.LEAD,
    EntityType.COMPANY,
    EntityType.PERSON,
    EntityType.CONTRACT
  ].map((entityType) => entityTypeToPrefix[entityType])
);

/**
 * Build entityIds for the pre-multi-link form, which only holds one entity per
 * fixed slot. A slot is seeded from the first id of its prefix, so only that id
 * is replaced or removed when the slot changes — every other id, including
 * extra links of the same type and types with no slot at all (partners, partner
 * contacts), is carried over untouched. Opening and saving a multi-link task
 * without the flag therefore never drops links the user cannot see.
 */
export const buildLegacyEntityIds = (
  originalEntityIds: string[] | null | undefined,
  slotIds: (string | undefined)[]
): string[] => {
  const original = originalEntityIds ?? [];
  const slotIdByPrefix = new Map<string, string>();
  slotIds.forEach((slotId) => {
    const trimmed = slotId?.trim();
    if (trimmed) slotIdByPrefix.set(getPrefix(trimmed), trimmed);
  });

  const seededIdByPrefix = new Map<string, string>();
  original.forEach((entityId) => {
    const prefix = getPrefix(entityId);
    if (!seededIdByPrefix.has(prefix)) seededIdByPrefix.set(prefix, entityId);
  });

  const result = original.filter((entityId) => {
    const prefix = getPrefix(entityId);
    if (!legacySlotPrefixes.has(prefix)) return true;
    if (seededIdByPrefix.get(prefix) !== entityId) return true;
    return slotIdByPrefix.get(prefix) === entityId;
  });

  slotIdByPrefix.forEach((slotId) => {
    if (!result.includes(slotId)) result.push(slotId);
  });

  return result;
};

export const getOriginLink = (
  entityType: EntityType | undefined,
  entityData: EntityData | undefined
): TaskLink | null => {
  if (!entityType || !entityData?.id) return null;
  const entityName =
    entityType === EntityType.PERSON
      ? getFullName(entityData as PersonData)
      : entityType === EntityType.PARTNER_CONTACT
        ? getPartnerContactFullName(entityData as PartnerContactData)
        : (entityData.name as string) ?? "";
  return { entityId: entityData.id, entityName, entityType };
};
